// ============ MARKET LEARNING · daily digest — dashboard card ============
// Renders the market-intel digest that the daily (05:00 Thailand) market-learning
// scheduled task writes to research/market-learning/latest.json from nopoly.ai
// data. Served by /api/market-learning; the card hides itself when the file is
// absent (so the dashboard degrades cleanly before the first run).
import React, { useEffect, useState } from 'react';
import { SecHead } from './core';
import { useT, useLang } from '../lib/i18n';

interface Section { icon?: string; title: string; items: string[]; }
interface Digest {
  updatedAt?: string;
  asOf?: string;
  takeaway?: string;
  sections?: Section[];
  footnote?: string;
}

export function MarketLearningCard() {
  const t = useT();
  const { lang } = useLang();
  const [d, setD] = useState<Digest | null>(null);

  useEffect(() => {
    let alive = true;
    const load = () => fetch('/api/market-learning').then(r => (r.ok ? r.json() : null)).then(v => { if (alive) setD(v); }).catch(() => {});
    load();
    // Re-check every 30 min so the daily (05:00) run shows without a page reload.
    const id = setInterval(load, 1800000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!d || !d.sections?.length) return null; // no digest file → no card

  const when = d.updatedAt
    ? new Date(d.updatedAt).toLocaleString(lang === 'en' ? 'en-US' : 'th-TH', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '';

  return (
    <div className="mm-card glass mm-mlearn">
      <SecHead th="บทเรียนตลาดรายวัน" en="DAILY MARKET LEARNING"></SecHead>

      <div className="mm-mlearn-meta mono dim">
        <span>⟳ {t('อัปเดต', 'updated')} {when}</span>
        {d.asOf && <span>· {d.asOf}</span>}
      </div>

      {d.takeaway && (
        <div className="mm-mlearn-takeaway">
          <span className="mm-mlearn-key mono">แก่น</span>
          <p>{d.takeaway}</p>
        </div>
      )}

      <div className="mm-mlearn-grid">
        {d.sections!.map((s, i) => (
          <div className="mm-mlearn-sec" key={i}>
            <b className="mm-mlearn-sectitle">{s.icon && <span>{s.icon} </span>}{s.title}</b>
            <ul>
              {s.items.map((it, j) => <li key={j}>{it}</li>)}
            </ul>
          </div>
        ))}
      </div>

      {d.footnote && <p className="mm-footnote dim">{d.footnote}</p>}
    </div>
  );
}
